import { Component } from '@theme/component';

/** Owns accessible navigation dismissal and header measurements. */
class ZoobiHeader extends Component {
  #controller;
  #resize;
  #animations = new Map();
  #hoverTimer;
  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#resize?.disconnect();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const accountDialog = this.querySelector('[data-account-dialog]');
      if (target.closest('[data-open-account]') && accountDialog) { event.preventDefault(); accountDialog.showModal(); }
      if (target.closest('[data-close-account]') && accountDialog) { accountDialog.close(); }
      const summary = target.closest('.zhd__dropdown > summary, .zhd__mobile-group > summary, .zhd__mobile > summary');
      if (summary) {
        event.preventDefault();
        const details = summary.parentElement;
        if (!details.open) for (const other of this.querySelectorAll('.zhd__dropdown[open]')) if (other !== details) this.#setOpen(other, false);
        this.#setOpen(details, !details.open);
      }
      if (target.closest('[data-close-menu]')) this.#close(true);
    }, { signal });
    this.querySelector('[data-account-dialog]')?.addEventListener('close', () => this.querySelector('[data-open-account]')?.focus(), { signal });
    this.addEventListener('toggle', (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open || !details.matches('.zhd__dropdown')) return;
      for (const other of this.querySelectorAll('.zhd__dropdown[open]')) if (other !== details) other.open = false;
    }, { capture: true, signal });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.#close(true); }, { signal });
    document.addEventListener('click', (event) => { if (event.target instanceof Node && !this.contains(event.target)) this.#close(false); }, { signal });
    this.addEventListener('pointerover', (event) => {
      if (this.dataset.hover !== 'true' || event.pointerType === 'touch' || !matchMedia('(min-width:750px)').matches) return;
      const details = event.target.closest('.zhd__dropdown');
      if (!details) return;
      clearTimeout(this.#hoverTimer);
      this.#hoverTimer = setTimeout(() => { for (const other of this.querySelectorAll('.zhd__dropdown[open]')) if (other !== details) this.#setOpen(other, false); this.#setOpen(details, true); }, 120);
    }, { signal });
    this.addEventListener('pointerleave', () => { clearTimeout(this.#hoverTimer); if (this.dataset.hover === 'true') this.#hoverTimer = setTimeout(() => this.#close(false), 180); }, { signal });
    this.addEventListener('pointerenter', () => clearTimeout(this.#hoverTimer), { signal });
    this.#resize = new ResizeObserver(() => {
      document.body.style.setProperty('--header-height', `${this.getBoundingClientRect().height}px`);
      const group = document.getElementById('header-group');
      if (group) document.body.style.setProperty('--header-group-height', `${group.getBoundingClientRect().height}px`);
    });
    this.#resize.observe(this);
  }
  #setOpen(details, open) {
    const old = this.#animations.get(details);
    old?.cancel();
    this.#animations.delete(details);
    const panel = details.querySelector(':scope > .zhd__mega, :scope > .zhd__mobile-panel');
    const duration = Number(this.dataset.duration) || 280;
    if (!panel || this.dataset.motion !== 'true' || matchMedia('(prefers-reduced-motion: reduce)').matches) { details.open = open; return; }
    if (open) details.open = true;
    const frames = open ? [{ opacity: 0, transform: 'translateY(-8px)' }, { opacity: 1, transform: 'translateY(0)' }] : [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-4px)' }];
    const animation = panel.animate(frames, { duration: open ? duration : duration * .7, easing: 'cubic-bezier(.22,1,.36,1)' });
    this.#animations.set(details, animation);
    animation.finished.then(() => { if (this.#animations.get(details) !== animation) return; if (!open) details.open = false; this.#animations.delete(details); }).catch(() => {});
  }
  #close(restore) {
    const open = this.querySelector('details[open]');
    for (const details of this.querySelectorAll('details[open]')) this.#setOpen(details, false);
    if (restore) open?.querySelector('summary')?.focus();
  }
  disconnectedCallback() {
    clearTimeout(this.#hoverTimer);
    for (const animation of this.#animations.values()) animation.cancel();
    this.#animations.clear();
    this.#controller?.abort();
    this.#resize?.disconnect();
    super.disconnectedCallback();
  }
}
if (!customElements.get('zoobi-header')) customElements.define('zoobi-header', ZoobiHeader);
